import Config from '@config';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared/index';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  AdvanceInvoice,
  AdvanceInvoiceDetails,
  AdvanceInvoicePreview,
  AdvanceInvoicePreviewParams,
  AdvanceInvoiceRequestBody,
  AdvanceInvoicesSummary,
} from './types';
import { expandNumberOrderBy } from './utils';

export const advanceInvoicesApi = createApi({
  reducerPath: 'advanceInvoicesApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/advance-invoices',
  }),
  tagTypes: ['AdvanceInvoices', 'AdvanceInvoice', 'AdvanceInvoicePreview'],
  endpoints: builder => ({
    getAdvanceInvoicesI: builder.infiniteQuery<
      AdvanceInvoice[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '/',
        params: {
          ...queryArg,
          orderBy: expandNumberOrderBy(queryArg ? queryArg.orderBy : undefined),
          ...pageParam,
        },
      }),
      providesTags: ['AdvanceInvoices'],
    }),
    getAdvanceInvoiceById: builder.query<AdvanceInvoiceDetails, string>({
      query: id => ({ url: `/${id}` }),
      providesTags: (_, __, id) => [{ type: 'AdvanceInvoice', id }],
    }),
    getAdvanceInvoicePreview: builder.query<
      AdvanceInvoicePreview,
      AdvanceInvoicePreviewParams
    >({
      query: params => ({ url: '/preview', params }),
      providesTags: ['AdvanceInvoicePreview'],
    }),
    getAdvanceInvoicesSummary: builder.query<
      AdvanceInvoicesSummary,
      { filter?: string }
    >({
      query: params => ({ url: '/summary', params }),
      providesTags: ['AdvanceInvoices'],
    }),
    createAdvanceInvoice: builder.mutation<string, AdvanceInvoiceRequestBody>({
      query: data => ({ url: '/', method: 'POST', data }),
      invalidatesTags: ['AdvanceInvoices', 'AdvanceInvoicePreview'],
    }),
    cancelAdvanceInvoice: builder.mutation<void, string>({
      query: id => ({ url: `/${id}/cancel`, method: 'POST' }),
      invalidatesTags: (_, __, id) => [
        'AdvanceInvoices',
        'AdvanceInvoicePreview',
        { type: 'AdvanceInvoice', id },
      ],
    }),
  }),
});

export const {
  useGetAdvanceInvoiceByIdQuery,
  useGetAdvanceInvoicePreviewQuery,
  useGetAdvanceInvoicesSummaryQuery,
  useCreateAdvanceInvoiceMutation,
  useCancelAdvanceInvoiceMutation,
} = advanceInvoicesApi;

export const useGetAdvanceInvoicesIInfiniteQuery = normalizeInfiniteQueryData(
  advanceInvoicesApi.useGetAdvanceInvoicesIInfiniteQuery,
);
